import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import { ChevronLeftSmall, ChevronRightSmall } from 'app/features/leads/components/guide/guide-icons';
import Button from 'app/ui/components/atoms/button';
import Typography from 'app/ui/components/atoms/typography';

export type LeadsPaginationFooterProps = {
  currentPage: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  isLoading?: boolean;
};

const Footer = styled.footer`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: ${({ theme: { spacing } }) => spacing(2)};
  row-gap: ${({ theme: { spacing } }) => spacing(3)};
  align-items: center;
  width: 100%;
  padding-top: ${({ theme: { spacing } }) => spacing(6)};

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      grid-template-columns: 1fr auto;
      grid-template-rows: auto;
      row-gap: 0;
      column-gap: ${spacing(4)};
    `,
    )}
`;

const PaginationLabel = styled(Typography)`
  grid-column: 1;
  grid-row: 1;
  justify-self: start;
`;

const ButtonsGroup = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  grid-column: 1 / -1;
  grid-row: 2;
  width: 100%;

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      grid-column: 2;
      grid-row: 1;
      width: auto;
      justify-self: end;
    `,
    )}
`;

const BackButtonIcon = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.semantic.text_small_elements};
`;

const BackButtonWrapper = styled.div`
  flex: 1;
  min-width: 0;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme: { spacing } }) => spacing(2)};
    width: 100%;
    color: ${({ theme }) => theme.semantic.text_small_elements};
  }

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      flex: none;

      button {
        width: auto;
      }
    `,
    )}
`;

const NextButtonWrapper = styled.div`
  flex: 1;
  min-width: 0;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme: { spacing } }) => spacing(2)};
    width: 100%;
  }

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      flex: none;

      button {
        width: auto;
      }
    `,
    )}
`;

export const LeadsPaginationFooter: React.FC<LeadsPaginationFooterProps> = ({
  currentPage,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  isLoading = false,
}) => {
  const intl = useIntl();
  const { semantic } = useTheme();

  const paginationLabel = intl.formatMessage({ id: 'leads.pagination.page' }, { currentPage });
  const backLabel = intl.formatMessage({ id: 'leads.pagination.previous' });
  const nextLabel = intl.formatMessage({ id: 'leads.pagination.next' });

  return (
    <Footer data-testid="leads-pagination-footer">
      <PaginationLabel variant="body3" color={semantic.text_small_elements} data-testid="leads-pagination-label">
        {paginationLabel}
      </PaginationLabel>
      <ButtonsGroup>
        {hasPrevious ? (
          <BackButtonWrapper data-testid="leads-pagination-back">
            <Button
              type="secondary-white"
              onClick={onPrevious}
              label={backLabel}
              state={isLoading ? 'disabled' : 'default'}
              isLoading={isLoading}
              icon={
                <BackButtonIcon>
                  <ChevronLeftSmall />
                </BackButtonIcon>
              }
              iconPosition="left"
              className="leads-pagination-back"
            />
          </BackButtonWrapper>
        ) : null}
        {hasNext ? (
          <NextButtonWrapper data-testid="leads-pagination-next">
            <Button
              type="primary"
              onClick={onNext}
              label={nextLabel}
              state={isLoading ? 'disabled' : 'default'}
              isLoading={isLoading}
              icon={<ChevronRightSmall />}
              iconPosition="right"
              className="leads-pagination-next"
            />
          </NextButtonWrapper>
        ) : null}
      </ButtonsGroup>
    </Footer>
  );
};
